'use client';
import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';

const PHONE = '9492935721';
const PHONE_DISPLAY = '(949) 293-5721';
const SMS_LINK = `sms:${PHONE}`;
const CALL_LINK = `tel:+1${PHONE}`;

function smsLink(message: string) {
  return `sms:${PHONE}?body=${encodeURIComponent(message)}`;
}

const mainServices = [
  {
    id: 'full-detail',
    badge: 'Most Popular',
    title: 'DA1MAMBA Full Detail',
    subtitle: 'Complete interior + exterior reset — Newport Beach, Irvine & Orange County',
    description: 'The full reset your car has been waiting for. Interior shampoo & steam, clay bar decontamination, and gloss protection — applied with brand-new microfiber towels on every single vehicle, no exceptions.',
    smsMessage: "Hi DA1MAMBA! I'm interested in a Full Detail for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    exterior: [
      'Touchless pre-rinse & premium foam bath',
      'Wheel, barrel, caliper & tire deep clean',
      'Contact wash with safe 2-bucket method',
      'Clay bar & chemical decontamination',
      'Gentle drying — air + plush microfiber',
      'Trim clean & dress',
      'Streak-free glass',
      'Premium sealant or spray ceramic topper',
    ],
    interior: [
      'Comprehensive vacuum (seats, carpets, trunk)',
      'Shampoo & steam treatment',
      'Leather clean & condition (or fabric deep clean)',
      'Panels, vents, buttons detailed',
      'Streak-free interior glass & mirrors',
      'Professional deodorize & fresh scent',
    ],
    enhancements: [
      'Pet hair removal & stain extraction',
      'Ozone/odor neutralization',
      'Water-spot, bug & sap removal',
      'Headlight restoration',
      'Engine bay cleaning',
      'Ceramic coating protection',
      'Paint correction',
    ],
  },
];

const individualServices = [
  {
    title: 'Mini Detail',
    subtitle: 'Exterior + Light Interior',
    description: 'The smart choice for regular upkeep between full details — keeps your car looking sharp without the full commitment.',
    smsMessage: "Hi DA1MAMBA! I'm interested in a Mini Detail for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    includes: [
      'Ceramic foam wash',
      '2 bucket hand wash',
      'Spot free rinse',
      'Air pressure & microfiber towel dry',
      'Tire & trim dressing',
      'Rim cleaning',
      'Vacuum floors and mats',
      'Interior and exterior window streak free cleaning',
    ],
  },
  {
    title: 'Full Exterior Detail',
    subtitle: 'Luxury exterior detailing — Newport Beach & Irvine',
    description: 'Paint-safe methods and professional-grade products for a crisp, hydrophobic finish that turns heads.',
    smsMessage: "Hi DA1MAMBA! I'm interested in a Full Exterior Detail for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    includes: [
      'Touchless pre-rinse & premium foam bath',
      'Wheel, barrel, caliper & tire deep clean',
      'Hand wash with two-bucket method',
      'Clay bar & chemical decontamination',
      'Gentle drying — air + plush microfiber',
      'Trim & plastics cleaned and dressed',
      'Premium sealant or spray ceramic topper',
    ],
  },
  {
    title: 'Full Interior Detail',
    subtitle: 'Luxury interior detailing — Newport Beach & Irvine',
    description: 'A crisp, hygienic cabin restored with meticulous care — every surface treated with the right chemistry and technique.',
    smsMessage: "Hi DA1MAMBA! I'm interested in a Full Interior Detail for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    includes: [
      'Precision vacuum (seats, carpets, trunk)',
      'Shampoo & steam treatment',
      'Targeted stain extraction',
      'Leather clean & condition',
      'Panels, vents, buttons detailed',
      'Streak-free glass & sanitization',
    ],
  },
  {
    title: 'Boat Detailing',
    subtitle: 'Professional marine vessel detailing — Orange County',
    description: 'Complete marine detailing for boats, yachts, and watercraft. We treat your vessel with the same care we give to a Rolls-Royce.',
    smsMessage: "Hi DA1MAMBA! I'm interested in Boat Detailing for my [Year, Make/Model, Length, Type of vessel]. Can you send me a quote? My location is [City/Marina].",
    includes: [
      'Hull cleaning',
      'Deck washing and treatment',
      'Interior cabin detailing',
      'Upholstery cleaning and protection',
      'Metal and chrome polishing',
    ],
  },
];

const specialistServices = [
  {
    title: 'Paint Correction',
    subtitle: 'Dealership-grade paint correction — Newport Beach, Irvine & Orange County',
    description: 'Paint correction restores true clarity and depth by safely removing swirls, light scratches, haze, and oxidation from your clear coat. We use precision techniques, premium compounds, and paint depth gauges to measure results — no guessing, no shortcuts.',
    smsMessage: "Hi DA1MAMBA! I'm interested in Paint Correction for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    levels: [
      { name: 'Level 1 — Enhancement (One-Step Polish)', desc: 'Refines up to 50–70% of visible defects; boosts gloss and clarity for newer or well-maintained finishes.' },
      { name: 'Level 2 — Correction (Two-Step)', desc: 'Compound + polish to remove heavier swirls and light scratches; 70–90% defect removal on most paints.' },
      { name: 'Level 3 — Signature (Multi-Step)', desc: 'Tailored multi-stage correction for severe defects on sensitive or hard clear coats.' },
    ],
    fixes: [
      'Wash marring, holograms & swirl marks',
      'Light to moderate clear-coat scratches',
      'Oxidation, haze & dullness',
      'Water spots & minor etching',
    ],
    trusted: 'Ferrari · Lamborghini · McLaren · Porsche · Bentley · Rolls-Royce · Mercedes-AMG · BMW M · Audi RS · Tesla',
  },
  {
    title: 'Ceramic Coating',
    subtitle: 'Premium ceramic coating — Newport Beach, Irvine & Orange County',
    description: 'DA1MAMBA applies dealership-grade ceramic coatings that create a long-term hydrophobic barrier — enhancing gloss, cutting maintenance time, blocking UV damage, and keeping your vehicle cleaner between washes. This isn\'t a wax. It\'s protection that lasts years.',
    smsMessage: "Hi DA1MAMBA! I'm interested in Ceramic Coating for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    coats: [
      'Paintwork',
      'Wheels, calipers & rims',
      'Glass & windows',
      'Trim & plastics',
      'Interiors (leather, fabric, plastics)',
    ],
    options: 'Entry level 6-month & premium 2+ year options available.',
    trusted: 'BMW · Mercedes-Benz · Audi · Porsche · Lamborghini · Ferrari · McLaren · Tesla · Rolls-Royce · Bentley · High-end SUVs',
  },
];

const packages = [
  {
    name: 'Platinum Deluxe',
    smsMessage: "Hi DA1MAMBA! I'm interested in the Platinum Deluxe package for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    includes: [
      'Full interior & exterior detail',
      'Clay bar decontamination',
      '1-step polish',
      'Ceramic sealant',
    ],
  },
  {
    name: 'Luxury White Glove',
    badge: 'Premium',
    smsMessage: "Hi DA1MAMBA! I'm interested in the Luxury White Glove package for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    includes: [
      'Full interior shampoo + leather treatment',
      'Paint gloss enhancement polish',
      'Ceramic coating application',
      'Engine bay cleaning',
    ],
  },
  {
    name: 'Ultimate Experience',
    badge: 'Signature',
    smsMessage: "Hi DA1MAMBA! I'm interested in the Ultimate Experience package for my [Year, Make, Model, Color]. Can you send me a quote? My location is [City/Address].",
    includes: [
      'Multi-stage paint correction',
      'Premium ceramic coating (2+ year protection)',
      'Complete interior restoration',
      'Underhood detailing',
    ],
  },
];

const addOns = [
  'Clay Bar Decontamination',
  'Headlight Restoration',
  'Engine Bay Cleaning',
  'Pet Hair Removal',
  'Exterior Bug/Sap Removal',
  'Odor Treatment',
  'Tire Patching',
  'Window PPF Removal',
];

export default function ServicesPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal:not(.visible)').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.06 }
    );
    document.querySelectorAll('section')?.forEach((s) => obs?.observe(s));
    return () => obs?.disconnect();
  }, []);

  return (
    <div style={{ background: 'var(--mamba-black)', minHeight: '100vh' }}>
      <Header />
      <main>
        {/* Hero */}
        <section
          style={{
            paddingTop: '0',
            paddingBottom: '0',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '55vh',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {/* Background image */}
          <div className="absolute inset-0">
            <Image
              src="https://da1mambadetailing.com/assets/280047261_531716588413514_7693021305676078627_n_1751758254505.jpg"
              alt="Lamborghini paint correction masking before treatment by DA1MAMBA"
              fill
              className="object-cover object-center"
              priority
              quality={90}
            />
          </div>
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(100deg, rgba(5,5,5,0.97) 0%, rgba(5,5,5,0.8) 50%, rgba(5,5,5,0.4) 100%)' }}
          />
          <div
            className="absolute bottom-0 inset-x-0"
            style={{ height: '50%', background: 'linear-gradient(to top, rgba(10,10,10,1) 0%, transparent 100%)' }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 50% 80% at 20% 50%, rgba(107,33,168,0.12) 0%, transparent 70%)' }}
          />
          <div className="max-w-7xl mx-auto px-6 relative z-10 pb-20 pt-40 w-full">
            <div className="label-gold mb-5 reveal">
              <span style={{ width: '40px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Exclusive Services
            </div>
            <h1
              className="reveal reveal-delay-1"
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(44px, 7vw, 88px)',
                lineHeight: '0.95',
                letterSpacing: '-0.03em',
                color: 'var(--white)',
                marginBottom: '20px',
              }}
            >
              Every service.<br />
              <span
                style={{
                  background: 'linear-gradient(135deg, var(--mamba-purple-bright), #9333ea 50%, var(--mamba-gold-light))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                At your door.
              </span>
            </h1>
            <p className="reveal reveal-delay-2" style={{ color: 'rgba(209,213,219,0.8)', fontSize: '18px', lineHeight: '1.65', fontFamily: 'DM Sans, sans-serif', maxWidth: '560px', marginBottom: '32px' }}>
              From maintenance washes to multi-stage paint correction and ceramic coating — every service is mobile, every result is showroom-quality.
            </p>
            <div className="flex gap-3 reveal reveal-delay-3">
              <a href={SMS_LINK} className="btn-primary" style={{ fontSize: '13px', padding: '16px 32px' }}>
                Text for a Quote
              </a>
              <a href={CALL_LINK} className="btn-gold" style={{ fontSize: '13px', padding: '15px 28px' }}>
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>

        {/* Featured — Full Detail */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-black)' }}>
          <div className="max-w-7xl mx-auto px-6">
            {mainServices?.map((s) => (
              <div key={s?.id}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
                  <div>
                    <div className="label-gold mb-3 reveal">
                      <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
                      {s?.badge}
                    </div>
                    <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)' }}>
                      {s?.title}
                    </h2>
                    <p className="reveal reveal-delay-2" style={{ color: 'var(--gray-dim)', fontSize: '15px', fontFamily: 'DM Sans, sans-serif', marginTop: '8px' }}>
                      {s?.subtitle}
                    </p>
                  </div>
                  <div className="flex gap-3 reveal reveal-delay-2">
                    <a href={smsLink(s?.smsMessage)} className="btn-primary" style={{ fontSize: '12px', padding: '12px 22px' }}>
                      Text for Quote
                    </a>
                    <a href={CALL_LINK} className="btn-ghost" style={{ fontSize: '12px', padding: '12px 22px' }}>
                      Call Now
                    </a>
                  </div>
                </div>

                <p className="reveal" style={{ color: 'var(--cool-gray)', fontSize: '16px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '40px', maxWidth: '700px' }}>
                  {s?.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Exterior */}
                  <div
                    className="reveal"
                    style={{ background: 'var(--mamba-graphite-mid)', border: '1px solid rgba(107,33,168,0.15)', padding: '32px' }}
                  >
                    <div className="label-purple mb-4">Exterior Included</div>
                    <div className="flex flex-col gap-3">
                      {s?.exterior?.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-purple-bright)', flexShrink: 0, marginTop: '7px' }} />
                          <span style={{ color: 'var(--cool-gray)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.5' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Interior */}
                  <div
                    className="reveal reveal-delay-1"
                    style={{ background: 'var(--mamba-graphite-mid)', border: '1px solid rgba(107,33,168,0.15)', padding: '32px' }}
                  >
                    <div className="label-purple mb-4">Interior Included</div>
                    <div className="flex flex-col gap-3">
                      {s?.interior?.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-purple-bright)', flexShrink: 0, marginTop: '7px' }} />
                          <span style={{ color: 'var(--cool-gray)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.5' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Enhancements */}
                  <div
                    className="reveal reveal-delay-2"
                    style={{ background: 'var(--mamba-graphite-mid)', border: '1px solid rgba(212,175,55,0.15)', padding: '32px' }}
                  >
                    <div className="label-gold mb-4">Popular Enhancements</div>
                    <div className="flex flex-col gap-3">
                      {s?.enhancements?.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-gold-light)', flexShrink: 0, marginTop: '7px' }} />
                          <span style={{ color: 'var(--cool-gray)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.5' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="section-divider" />

        {/* Individual Services */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-graphite)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-gold mb-4 reveal">
              <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Individual Services
            </div>
            <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '48px' }}>
              Choose what your<br />
              <span className="text-gradient-purple">vehicle actually needs.</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {individualServices?.map((s, i) => (
                <div
                  key={i}
                  className="service-card reveal"
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--white)', marginBottom: '4px' }}>
                    {s?.title}
                  </h3>
                  <p style={{ color: 'var(--mamba-gold-light)', fontSize: '12px', fontFamily: 'Manrope, sans-serif', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '12px' }}>
                    {s?.subtitle}
                  </p>
                  <p style={{ color: 'var(--gray-dim)', fontSize: '14px', lineHeight: '1.6', fontFamily: 'DM Sans, sans-serif', marginBottom: '20px' }}>
                    {s?.description}
                  </p>
                  <div className="flex flex-col gap-2 mb-6">
                    {s?.includes?.map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-purple-bright)', flexShrink: 0, marginTop: '7px' }} />
                        <span style={{ color: 'var(--cool-gray)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <a href={smsLink(s?.smsMessage)} className="btn-primary" style={{ fontSize: '11px', padding: '10px 16px' }}>
                      Text Quote
                    </a>
                    <a href={CALL_LINK} className="btn-ghost" style={{ fontSize: '11px', padding: '10px 16px' }}>
                      Call Now
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Specialist Services */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-black)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-gold mb-4 reveal">
              <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Specialist Services
            </div>
            <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '48px' }}>
              Specialist services for<br />
              <span className="text-gradient-purple">vehicles that demand more.</span>
            </h2>

            <div className="flex flex-col gap-12">
              {specialistServices?.map((s, i) => (
                <div
                  key={i}
                  className="reveal"
                  style={{
                    background: 'var(--mamba-graphite-mid)',
                    border: '1px solid rgba(107,33,168,0.2)',
                    padding: '48px',
                  }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                      <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--white)', marginBottom: '8px' }}>
                        {s?.title}
                      </h3>
                      <p style={{ color: 'var(--mamba-gold-light)', fontSize: '12px', fontFamily: 'Manrope, sans-serif', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '16px' }}>
                        {s?.subtitle}
                      </p>
                      <p style={{ color: 'var(--cool-gray)', fontSize: '15px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '24px' }}>
                        {s?.description}
                      </p>
                      {'options' in s && (
                        <p style={{ color: 'var(--mamba-purple-bright)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif', marginBottom: '16px' }}>
                          {s?.options}
                        </p>
                      )}
                      <div
                        style={{
                          background: 'rgba(107,33,168,0.08)',
                          border: '1px solid rgba(107,33,168,0.2)',
                          padding: '16px 20px',
                          marginBottom: '24px',
                        }}
                      >
                        <div className="label-gold mb-2" style={{ fontSize: '10px' }}>Trusted On</div>
                        <p style={{ color: 'var(--gray-dim)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.6' }}>
                          {s?.trusted}
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <a href={smsLink(s?.smsMessage)} className="btn-primary" style={{ fontSize: '12px', padding: '12px 22px' }}>
                          Text for Quote
                        </a>
                        <a href={CALL_LINK} className="btn-ghost" style={{ fontSize: '12px', padding: '12px 22px' }}>
                          Call Now
                        </a>
                      </div>
                    </div>

                    <div>
                      {'levels' in s && s?.levels && (
                        <div className="flex flex-col gap-4">
                          <div className="label-purple mb-2">Service Levels</div>
                          {s?.levels?.map((level) => (
                            <div
                              key={level?.name}
                              style={{
                                background: 'var(--mamba-graphite)',
                                border: '1px solid rgba(107,33,168,0.15)',
                                padding: '20px',
                              }}
                            >
                              <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--white)', marginBottom: '6px' }}>
                                {level?.name}
                              </div>
                              <div style={{ color: 'var(--gray-dim)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.5' }}>
                                {level?.desc}
                              </div>
                            </div>
                          ))}
                          {'fixes' in s && s?.fixes && (
                            <div style={{ marginTop: '8px' }}>
                              <div className="label-gold mb-3" style={{ fontSize: '10px' }}>What It Fixes</div>
                              <div className="flex flex-col gap-2">
                                {s?.fixes?.map((fix) => (
                                  <div key={fix} className="flex items-start gap-2">
                                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-gold-light)', flexShrink: 0, marginTop: '7px' }} />
                                    <span style={{ color: 'var(--cool-gray)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' }}>{fix}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {'coats' in s && s?.coats && (
                        <div>
                          <div className="label-purple mb-4">What We Coat</div>
                          <div className="flex flex-col gap-3">
                            {s?.coats?.map((item) => (
                              <div key={item} className="flex items-start gap-2">
                                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-purple-bright)', flexShrink: 0, marginTop: '7px' }} />
                                <span style={{ color: 'var(--cool-gray)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif' }}>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Premium Packages */}
        <section style={{ padding: '100px 0', background: 'var(--mamba-graphite)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-gold mb-4 reveal">
              <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Premium Packages
            </div>
            <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '12px' }}>
              Curated packages for<br />
              <span className="text-gradient-purple">the complete experience.</span>
            </h2>
            <p className="reveal reveal-delay-2" style={{ color: 'var(--gray-dim)', fontSize: '15px', fontFamily: 'DM Sans, sans-serif', marginBottom: '48px' }}>
              Multi-service packages designed for owners who want it all done right, in one visit.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages?.map((pkg, i) => (
                <div
                  key={i}
                  className="reveal"
                  style={{
                    transitionDelay: `${i * 0.1}s`,
                    background: i === 1 ? 'var(--mamba-graphite-mid)' : 'var(--mamba-graphite)',
                    border: i === 1 ? '1px solid rgba(107,33,168,0.4)' : '1px solid rgba(107,33,168,0.15)',
                    padding: '40px 32px',
                    position: 'relative',
                  }}
                >
                  {pkg?.badge && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-1px',
                        left: '32px',
                        background: 'var(--mamba-purple-bright)',
                        color: 'white',
                        fontSize: '10px',
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        padding: '4px 12px',
                      }}
                    >
                      {pkg?.badge}
                    </div>
                  )}
                  <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--white)', marginBottom: '20px', marginTop: pkg?.badge ? '12px' : '0' }}>
                    {pkg?.name}
                  </h3>
                  <div className="flex flex-col gap-3 mb-8">
                    {pkg?.includes?.map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-gold-light)', flexShrink: 0, marginTop: '7px' }} />
                        <span style={{ color: 'var(--cool-gray)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                  <a href={smsLink(pkg?.smsMessage)} className="btn-primary" style={{ fontSize: '12px', padding: '12px 22px', width: '100%', justifyContent: 'center' }}>
                    Text for Quote
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* Add-Ons */}
        <section style={{ padding: '80px 0', background: 'var(--mamba-black)' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="label-gold mb-4 reveal">
              <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Add-On Services
            </div>
            <h2 className="section-heading reveal reveal-delay-1" style={{ fontSize: 'clamp(24px, 3vw, 36px)', marginBottom: '32px' }}>
              Customize your detail.
            </h2>
            <div className="flex flex-wrap gap-3 reveal reveal-delay-2">
              {addOns?.map((addon) => (
                <span
                  key={addon}
                  style={{
                    background: 'var(--mamba-graphite-mid)',
                    border: '1px solid rgba(107,33,168,0.2)',
                    color: 'var(--cool-gray)',
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '14px',
                    padding: '10px 20px',
                  }}
                >
                  {addon}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section
          style={{
            padding: '80px 0',
            background: 'var(--mamba-graphite)',
            borderTop: '1px solid rgba(107,33,168,0.2)',
          }}
        >
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="section-heading reveal" style={{ fontSize: 'clamp(28px, 4vw, 44px)', marginBottom: '12px' }}>
              Ready to see the<br />
              <span className="text-gradient-purple">difference?</span>
            </h2>
            <p className="reveal reveal-delay-1" style={{ color: 'var(--gray-dim)', fontSize: '16px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '12px' }}>
              Fast response. Transparent pricing. We come to your home or office.
            </p>
            <p className="reveal reveal-delay-2" style={{ color: 'var(--mamba-gold-light)', fontSize: '13px', fontFamily: 'Manrope, sans-serif', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '36px' }}>
              Serving Newport Beach, Irvine, and all of Orange County
            </p>
            <div className="flex flex-wrap gap-3 justify-center reveal reveal-delay-3">
              <a href={CALL_LINK} className="btn-gold" style={{ fontSize: '14px', padding: '16px 36px' }}>
                Call for a Quote — {PHONE_DISPLAY}
              </a>
              <a href={SMS_LINK} className="btn-primary" style={{ fontSize: '14px', padding: '16px 36px' }}>
                Text for a Quote
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
